'use client';

import { EntitlementsProvider } from '@contexts/EntitlementsContext';
import { useMicrocopy } from '@contexts/MicrocopyContext';
import {
  ProductListsProvider,
  useProductLists,
} from '@contexts/ProductListsContext';
import { Result, buildResultList } from '@coveo/atomic-react';
import { useController } from '@lib/hooks/useController';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { getMicrocopyValue } from '@lib/util/microcopy';
import { useEffect, useMemo, useRef } from 'react';
import { useEngine } from '../EngineContext';
import { ListingEmptyState } from '../ListingEmptyState';
import { ResultCard } from '../ResultCard';
import { ProductCardSkeleton } from '../Skeleton/ProductCardSkeleton';
import { useSearchLoading } from '../hooks/useSearchLoading';
import { ResultListProps } from './ResultList.types';

/**
 * Fields that must be included in Coveo search results.
 * Without this, Coveo returns only default fields (title, uri) and custom
 * fields like ec_name, ec_thumbnails, etc. come back as undefined.
 * Mirrors the fieldsToInclude from V1 AtomicSearchInterfaceWrapper.
 */
const FIELDS_TO_INCLUDE = [
  'ec_sale',
  'ec_name',
  'ec_thumbnails',
  'ec_price',
  'ec_price_filter',
  'ec_rating',
  'ec_rating_count',
  'ec_rating_sort',
  'ec_category',
  'publisher_name',
  'publisher_id',
  'ec_product_id',
  'ec_tags',
  'ec_is_new_release',
  'min_unity_version',
  'ec_flash_teaser_starts_at',
  'ec_flash_promo_starts_at',
];

export const GRID_CLASSES =
  'grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(18rem,1fr))] min-[430px]:[grid-template-columns:repeat(auto-fill,minmax(12rem,1fr))] sm:[grid-template-columns:repeat(auto-fill,minmax(12rem,1fr))] lg:[grid-template-columns:repeat(auto-fill,minmax(14rem,1fr))] 2xl:[grid-template-columns:repeat(auto-fill,minmax(13rem,1fr))]';

export const SKELETON_COUNT = 8;

export const ResultList = ({ className }: ResultListProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
  }>();
  const eng = useEngine();
  const controller = useMemo(
    () =>
      buildResultList(eng, {
        options: { fieldsToInclude: FIELDS_TO_INCLUDE },
      }),
    [eng]
  );
  const state = useController(controller);
  const { isInitialLoad } = useSearchLoading();

  if (isInitialLoad) {
    return (
      <div className={`${GRID_CLASSES} ${className ?? ''}`}>
        {Array.from({ length: SKELETON_COUNT }, (_, i) => (
          <ProductCardSkeleton key={`skeleton-${i}`} />
        ))}
      </div>
    );
  }

  if (!state.results || state.results.length === 0) {
    return (
      <ListingEmptyState
        title={getMicrocopyValue(
          searchLabelsMicrocopy,
          'search.no-results-title',
          'No results found'
        )}
        description={getMicrocopyValue(
          searchLabelsMicrocopy,
          'search.no-results-suggestion',
          'Try different keywords or remove filters'
        )}
      />
    );
  }

  return (
    <EntitlementsProvider>
      <ProductListsProvider>
        <ResultListInner results={state.results} className={className} />
      </ProductListsProvider>
    </EntitlementsProvider>
  );
};

type ResultListInnerProps = {
  results: Result[];
  className?: string;
};

const ResultListInner = ({ results, className }: ResultListInnerProps) => {
  const { fetchBatchProductMembership } = useProductLists();
  const prevKeyRef = useRef('');

  useEffect(() => {
    const productIds = results
      .map((r) => {
        const rawId = r.raw.ec_product_id;
        return rawId ? JSON.parse(rawId as string).toString() : '';
      })
      .filter(Boolean);

    const key = productIds.join(',');
    if (key && key !== prevKeyRef.current) {
      prevKeyRef.current = key;
      fetchBatchProductMembership(productIds);
    }
  }, [results, fetchBatchProductMembership]);

  return (
    <ul
      data-test="search-results-list"
      className={`${GRID_CLASSES} ${className ?? ''}`}
    >
      {results.map((result) => (
        <li key={result.raw.ec_product_id as string}>
          <ResultCard result={result} />
        </li>
      ))}
    </ul>
  );
};
